import { useEffect, useMemo, useState } from "react";
import { sfx } from "../lib/sound";
import { closeupPerson, look } from "@bd/art";
import { annexes, C, tips } from "@bd/content";
import { levelCap, priceIndex, researchOptions, rivalPrice, shelfSpace, yearOf } from "@bd/sim";
import { studio } from "../world/World";
import { useGame, me } from "../store";
import { dataUrl } from "../world/textures";
import { Icon } from "@bd/ui-kit";
import { t } from "../i18n";

const GRAN_LOOK = { ...look(7), hair: [196, 196, 206] as const, style: "bun" as const, glasses: true, shirt: [120, 72, 150] as const };

/**
 * Gran Okafor, a retired toy-shop owner: three tips, each the first time it matters, then she gets out of the way.
 * 0 = welcome (design your first game), 1 = playtest, 2 = print run, 3+ = done.
 */
export function Gran() {
  const step = useGame((s) => s.onboarding);
  const game = useGame((s) => s.game);
  const screen = useGame((s) => s.screen);
  const set = useGame((s) => s.set);
  const tool = useGame((s) => s.tool);
  const co = me(game);
  if (!co || tool) return null;
  // she moves on when you've actually done the thing, however you got there
  const done = co.stats.released > 0 || co.products.some((p) => p.orders.length > 0) ? 3 : co.projects.some((p) => p.playtests.length > 0) ? 2 : co.projects.length ? 1 : 0;
  const at = Math.max(step, done);
  if (at >= 3) return <FirstTimeTip />;
  const project = co.projects[0];
  let tip: { text: string; action?: { label: string; go: () => void } } | null = null;
  if (at === 0 && !project && co.products.length === 0 && screen?.kind !== "design") {
    tip = {
      text: t("Welcome, dear! Start small: animal trivia for families."),
      action: { label: t("Design"), go: () => set({ screen: { kind: "design" } }) },
    };
  } else if (at <= 1 && project?.phase === "playtest" && screen?.kind !== "playtest") {
    tip = {
      text: t("Watch people play before you print: friends are too kind."),
      action: { label: t("Watch"), go: () => set({ screen: { kind: "playtest", projectId: project.id }, onboarding: 2 }) },
    };
  } else if (at <= 2 && project?.phase === "ready" && screen?.kind !== "print") {
    tip = {
      text: t("Print near the low end of the forecast: selling out beats a full warehouse."),
      action: { label: t("Print"), go: () => set({ screen: { kind: "print", projectId: project.id }, onboarding: 3 }) },
    };
  }
  if (!tip) return null;
  const face = dataUrl(closeupPerson(GRAN_LOOK, { view: "front", mouth: "smile" }).crop(0, 0, 28, 30), 3);
  return (
    <div className="panel gran" role="note" data-testid="gran">
      <img src={face} alt={t("Gran Okafor")} width={64} height={68} style={{ imageRendering: "pixelated", flex: "none" }} />
      <div style={{ minWidth: 0 }} title={t("Gran Okafor · tip {n} of 3", { n: Math.min(3, at + 1) })}>
        <p className="gran-text" onClick={(e) => e.currentTarget.closest(".gran")?.classList.toggle("open")}>{tip.text}</p>
        <Voice text={tip.text} />
        {tip.action && <button className="btn primary small" onClick={tip.action.go} data-testid="gran-go">{tip.action.label}<Icon name="chevron" size={14} /></button>}
      </div>
      <button className="icon-btn gran-x" onClick={() => set({ onboarding: at + 1 })} aria-label={t("Thanks, Gran")} title={t("Thanks, Gran")} data-testid="gran-dismiss"><Icon name="close" size={16} /></button>
    </div>
  );
}

/**
 * After Gran, each system introduces itself once, the first time it matters: one card at a time, with a "Show me".
 * Which ones you've seen is remembered per season on this device.
 */
function FirstTimeTip() {
  const game = useGame((s) => s.game);
  const screen = useGame((s) => s.screen);
  const set = useGame((s) => s.set);
  const [seen, setSeen] = useState<string[]>(() => {
    const s = readSeen(game?.seed ?? "");
    // a game already well under way when first loaded here: the tips would only repeat what the player knows
    return s.length === 0 && (game?.week ?? 0) > 260 ? tips.map((x) => x.id) : s;
  });
  const co = me(game);
  // hooks before any early return; research options change at most once a week
  const week = game?.week ?? 0;
  const cheapest = useMemo(() => (co ? researchOptions(co, week).reduce((m, o) => Math.min(m, o.cost), Infinity) : Infinity), [co, week]);
  if (!co || !game || screen) return null;
  const idx = priceIndex(game.week);
  const year = yearOf(game.week);
  const woy = game.week % 52;
  const ever = co.products.find((p) => p.launched && p.evergreen && !p.retired && p.kind === "base");
  const sold = co.products.find((p) => p.launched && p.soldOutWeeks > 0 && p.stock === 0 && !p.retired);
  const sh = shelfSpace(game, co);
  const nextAnnex = annexes.find((a) => !(co.annexes ?? []).some((x) => x.kind === a.id) && (!a.year || year >= a.year));
  const triggers: Record<string, { on: boolean; game?: string; go: () => void }> = {
    office: { on: co.level === "garage" && co.cash > C.level.office.moveCost * idx * 1.2, go: () => set({ screen: { kind: "finances" } }) },
    evergreen: { on: !!ever, game: ever?.design.name, go: () => set({ screen: { kind: "products" } }) },
    soldout: { on: !!sold, game: sold?.design.name, go: () => set({ screen: { kind: "print", productId: sold!.id } }) },
    research: { on: co.research >= cheapest && !(co.researching ?? []).length && co.stats.released >= 2, go: () => set({ screen: { kind: "research" } }) },
    con: { on: year >= 1980 && woy >= 20 && woy < 30, go: () => set({ screen: { kind: "market", tab: "awards" } }) },
    licence: { on: co.licenceOffers.length > 0, go: () => set({ screen: { kind: "products" } }) },
    shelf: { on: sh.recent >= sh.slots, go: () => set({ screen: { kind: "products" } }) },
    annex: { on: (co.level === "studio" || co.level === "hq") && co.staff.length >= levelCap(co) && !!nextAnnex, go: () => { studio?.focusLot(nextAnnex!.id); set({ selectedLot: nextAnnex!.id }); } },
    buyout: { on: (co.level === "studio" || co.level === "hq") && game.rivals.some((r) => !r.bust && rivalPrice(game, r.id) < co.cash * 0.8), go: () => set({ screen: { kind: "finances" } }) },
    store: { on: (co.level === "studio" || co.level === "hq") && !(co.stores ?? []).length && co.cash > 60000 * idx, go: () => set({ screen: { kind: "finances" } }) },
  };
  const tip = tips.find((x) => triggers[x.id]?.on && !seen.includes(x.id));
  if (!tip) return null;
  const tr = triggers[tip.id];
  // content text: {game} is filled after translating, so the Ukrainian keeps its own word order
  const text = t(tip.text, { game: tr.game ?? t("Your game") });
  const dismiss = () => {
    const next = [...seen, tip.id];
    setSeen(next);
    try {
      localStorage.setItem(`bd:tips:${game.seed}`, JSON.stringify(next));
    } catch {
      // storage blocked: the tip may show again next time
    }
  };
  return (
    <div className="panel gran" role="note" aria-label={t("Tip")} data-testid="tip">
      <Icon name="bell" size={22} color="var(--gold)" style={{ flex: "none", marginTop: 2 }} />
      <div style={{ minWidth: 0 }}>
        <p className="gran-text" onClick={(e) => e.currentTarget.closest(".gran")?.classList.toggle("open")}>{text}</p>
        <Voice text={text} />
        <button className="btn primary small" onClick={() => { dismiss(); tr.go(); }} data-testid="tip-go">{t(tip.action)}<Icon name="chevron" size={14} /></button>
      </div>
      <button className="icon-btn gran-x" onClick={dismiss} aria-label={t("Got it")} title={t("Got it")} data-testid="tip-dismiss"><Icon name="close" size={16} /></button>
    </div>
  );
}

function readSeen(seed: string): string[] {
  try {
    return JSON.parse(localStorage.getItem(`bd:tips:${seed}`) ?? "[]") as string[];
  } catch {
    return [];
  }
}

/** Gran "speaks" in little blips when she has something new to say, one per word, a few at most. */
function Voice({ text }: { text: string }) {
  useEffect(() => {
    const words = Math.min(6, text.split(" ").length);
    const ids: number[] = [];
    for (let i = 0; i < words; i++) ids.push(window.setTimeout(() => sfx("blip", 0.9 + ((i * 37) % 5) * 0.06), 120 + i * 85));
    return () => ids.forEach((id) => window.clearTimeout(id));
  }, [text]);
  return null;
}
